'use client';

import { useMemo } from 'react';
import type { CartItem } from '@/lib/pricing';
import type { StockInfo, RegionPool } from '@/hooks/useCart';

// ── Public types ─────────────────────────────────────────────────────

export type StockStatus =
  | 'available'
  | 'low_stock'
  | 'fallback_region'
  | 'out_of_stock';

export interface ProductFeasibility {
  /** Region that will actually fulfil this product ('SG' | 'MY'). */
  effectiveRegion: string;
  /** Max units that can be added to the cart for this product. */
  maxQty: number;
  /** Whether at least one more unit can be added right now. */
  canAdd: boolean;
  /** High-level status for UI indicators. */
  status: StockStatus;
  /** Human-readable explanation when stock is constrained. */
  reason: string;
}

export interface UseStockEngineReturn {
  /** Per-product feasibility keyed by product ID. */
  feasibility: Record<string, ProductFeasibility>;
  /** Drop-in replacement for useCart's effectiveMaxQty. */
  effectiveMaxQty: Record<string, number>;
}

// ── Region try order ─────────────────────────────────────────────────

function regionsToTry(defaultRegion: string, playerRegion?: string, info?: StockInfo): string[] {
  if (playerRegion === 'ID') {
    if (info && (info.shellsId ?? 0) > 0) return ['ID'];
    if (defaultRegion === 'ID') return ['ID'];
  }
  const preferred = (defaultRegion || 'MY').toUpperCase();
  // ShellProduct.defaultRegion is an administrator routing decision, not a
  // hint. Keep the storefront's feasibility calculation on that exact pool
  // so it agrees with backend fulfillment after an SG/MY mapping change.
  return [preferred];
}

// ── Shell cost helper ────────────────────────────────────────────────

function shellCostForRegion(info: StockInfo, region: string): number {
  if (region === 'ID') return info.shellsId ?? 0;
  return region === 'MY' ? info.shellsMy : info.shellsSg;
}

// ── Bundle shell-per-region helper ───────────────────────────────────

/**
 * For a bundle with constituents, compute total shells consumed per region.
 * Returns e.g. { MY: 516, SG: 0 } for the 'vip' bundle.
 */
function bundleShellsByRegion(info: StockInfo): Record<string, number> {
  const byRegion: Record<string, number> = {};
  if (info.constituents && info.constituents.length > 0) {
    for (const c of info.constituents) {
      const r = (c.region || 'MY').toUpperCase();
      byRegion[r] = (byRegion[r] || 0) + c.shellsPerUnit * c.qty;
    }
  } else {
    // No constituents data — fall back to shellsNeeded in defaultRegion
    const r = (info.defaultRegion || 'MY').toUpperCase();
    byRegion[r] = info.shellsNeeded || 0;
  }
  return byRegion;
}

/**
 * Determine the "primary" region for a bundle (the one consuming the most shells).
 * Used for display/effectiveRegion only.
 */
function bundlePrimaryRegion(info: StockInfo): string {
  const byRegion = bundleShellsByRegion(info);
  let maxRegion = (info.defaultRegion || 'MY').toUpperCase();
  let maxShells = -1;
  for (const [region, shells] of Object.entries(byRegion)) {
    if (shells > maxShells) {
      maxShells = shells;
      maxRegion = region;
    }
  }
  return maxRegion;
}

// ── Low stock threshold ──────────────────────────────────────────────

/** If remaining capacity is ≤ this many units we flag "low_stock". */
const LOW_STOCK_THRESHOLD = 2;

// ── Hook ─────────────────────────────────────────────────────────────

/**
 * Simulates the backend's 3-tier fulfillment logic on the frontend so
 * that product cards and the cart can proactively show accurate stock
 * constraints without waiting for a checkout rejection.
 *
 * Algorithm (mirrors `order.service.ts` lines 555-699):
 *
 * For the product's database-configured `defaultRegion`:
 *   1. Per-account check — the richest single account in that region
 *      must have ≥ shellsPerUnit (since each order item executes on
 *      ONE account independently).
 *   2. Aggregate check — total available shells in the region (minus
 *      publicReserved and shells consumed by OTHER cart items) must
 *      cover at least one unit.
 *
 * The first region that passes both checks becomes the effectiveRegion.
 * maxQty is derived from the aggregate check, capped by the API maxQty.
 *
 * For BUNDLES, the backend is the source of truth for isAvailable and
 * maxQty. The stock engine uses constituent data to correctly attribute
 * shell consumption to the right region pools, ensuring that adding a
 * bundle reduces capacity for other products in those regions.
 */
export function useStockEngine(
  cart: Record<string, CartItem>,
  stockAvailability: Record<string, StockInfo>,
  regionPools: Record<string, RegionPool>,
  playerRegion?: string,
): UseStockEngineReturn {
  const result = useMemo(() => {
    const feasibility: Record<string, ProductFeasibility> = {};
    const effectiveMaxQty: Record<string, number> = {};

    // ── Pass 0: determine the effective region for every product ────
    // We need to know each product's effective region BEFORE computing
    // aggregate shell usage so that cross-region fallback is reflected
    // in the "other items' shells" calculation.
    //
    // Strategy: two passes.
    //   Pass 1 — tentatively assign an effective region per product
    //            (ignoring aggregate constraints from other items).
    //   Pass 2 — with regions assigned, compute per-item aggregate
    //            maxQty accounting for other items in the same region.

    // Pass 1: assign effectiveRegion per product (aggregate capacity check)
    const tentativeRegion: Record<string, string | null> = {};
    const tentativeShells: Record<string, number> = {}; // shells per unit in the chosen region
    // For bundles: per-region shell breakdown for correct pool accounting
    const bundleRegionShells: Record<string, Record<string, number>> = {};

    for (const [prodId, info] of Object.entries(stockAvailability)) {
      if (!info.isAvailable) {
        tentativeRegion[prodId] = null;
        tentativeShells[prodId] = 0;
        continue;
      }

      // ── Bundle handling ────────────────────────────────────────────
      // For bundles, the backend already verified per-constituent
      // feasibility. We trust isAvailable and maxQty from the backend.
      // We assign the "primary region" (where most shells are consumed)
      // as the effectiveRegion for display, and store per-region shell
      // breakdown for accurate pool accounting.
      if (info.isBundle) {
        const byRegion = bundleShellsByRegion(info);
        const primaryRegion = bundlePrimaryRegion(info);
        tentativeRegion[prodId] = primaryRegion;
        tentativeShells[prodId] = info.shellsNeeded || 0;
        bundleRegionShells[prodId] = byRegion;
        continue;
      }

      // ── GH product handling ───────────────────────────────────────
      if (info.defaultRegion === 'GAMERHUB') {
        tentativeRegion[prodId] = 'GAMERHUB';
        tentativeShells[prodId] = 0;
        continue;
      }

      // ── Single product handling ────────────────────────────────────
      const regions = regionsToTry(info.defaultRegion, playerRegion, info);

      let bestRegion: string | null = null;
      let bestShellsPerUnit = 0;
      let bestAggregateQty = -1;
      let firstAvailableRegion: string | null = null;

      for (const region of regions) {
        const pool = regionPools[region];
        if (!pool || pool.accounts === 0) continue;

        const shellsPerUnit = shellCostForRegion(info, region);

        // Record first region with a pool for free-item fallback
        if (firstAvailableRegion === null) {
          firstAvailableRegion = region;
        }

        if (shellsPerUnit <= 0) {
          continue; // product doesn't support this region — try next
        }

        // Use aggregate capacity (matching backend computeStockAvailability)
        // rather than per-account greedy check.  The backend evaluates all
        // fallback regions and picks the one with the highest maxQty.
        const totalAvailable = pool.accountShells.reduce(
          (a, b) => a + b,
          0,
        );
        const availableToPublic = Math.max(
          0,
          totalAvailable - (pool.publicReserved || 0),
        );
        const aggregateQty = Math.floor(
          availableToPublic / shellsPerUnit,
        );

        if (aggregateQty > bestAggregateQty) {
          bestAggregateQty = aggregateQty;
          bestRegion = region;
          bestShellsPerUnit = shellsPerUnit;
        }
      }

      // If no region with positive shell cost was found, fall back to
      // the first available region (free item / zero-shell product).
      if (bestRegion === null && firstAvailableRegion !== null) {
        bestRegion = firstAvailableRegion;
        bestShellsPerUnit = 0;
        bestAggregateQty = 999;
      }

      if (bestRegion !== null) {
        tentativeRegion[prodId] = bestRegion;
        tentativeShells[prodId] = bestShellsPerUnit;
      } else {
        tentativeRegion[prodId] = null;
        tentativeShells[prodId] = 0;
      }
    }

    // Pass 2: compute aggregate constraints per product
    // First, tally shells consumed by all cart items per region
    // (using their tentative regions).
    // For bundles, split shell costs across constituent regions.
    const cartShellsByRegion: Record<string, number> = {};
    for (const [itemId, item] of Object.entries(cart)) {
      if (item.isChannelItem) continue;
      const region = tentativeRegion[itemId];
      if (!region) continue;
      const qty = item.qty || 0;
      if (qty <= 0) continue;

      const info = stockAvailability[itemId];
      if (info?.isBundle && bundleRegionShells[itemId]) {
        // Bundle: split shell costs across constituent regions
        for (const [r, shellsPerUnit] of Object.entries(
          bundleRegionShells[itemId],
        )) {
          if (shellsPerUnit <= 0) continue;
          cartShellsByRegion[r] =
            (cartShellsByRegion[r] || 0) + shellsPerUnit * qty;
        }
      } else {
        // Single product: all shells go to the tentative region
        const shells = tentativeShells[itemId] || 0;
        if (shells <= 0) continue;
        cartShellsByRegion[region] =
          (cartShellsByRegion[region] || 0) + shells * qty;
      }
    }

    for (const [prodId, info] of Object.entries(stockAvailability)) {
      // ── Out of stock at the API level ────────────────────────────
      if (!info.isAvailable || tentativeRegion[prodId] === null) {
        feasibility[prodId] = {
          effectiveRegion: info.defaultRegion || 'MY',
          maxQty: 0,
          canAdd: false,
          status: 'out_of_stock',
          reason: 'Out of stock',
        };
        effectiveMaxQty[prodId] = 0;
        continue;
      }

      const effRegion = tentativeRegion[prodId]!;

      // ── Direct / API top-up product feasibility ──────────────────────
      if (effRegion === 'GAMERHUB' || info.defaultRegion === 'GAMERHUB' || (info.shellsNeeded === 0 && info.isAvailable)) {
        const apiMax = info.isAvailable ? (info.maxQty > 0 ? info.maxQty : 99) : 0;
        const publicReg = info.defaultRegion === 'GAMERHUB' ? (prodId.startsWith('id_') ? 'ID' : 'SG') : (info.defaultRegion || 'SG');
        const canAdd = info.isAvailable && apiMax > 0;
        feasibility[prodId] = {
          effectiveRegion: publicReg,
          maxQty: apiMax,
          canAdd,
          status: canAdd ? 'available' : 'out_of_stock',
          reason: canAdd ? 'In stock' : 'Out of stock',
        };
        effectiveMaxQty[prodId] = apiMax;
        continue;
      }

      // ── Bundle feasibility ─────────────────────────────────────────
      // The backend provides apiMax assuming no other items in the cart.
      // We further constrain it by aggregating shell usage from other
      // cart items in each constituent region, finding the bottleneck.
      if (info.isBundle) {
        let apiMax = info.maxQty;

        // If the bundle has constituent data, refine maxQty per-region
        if (info.constituents && info.constituents.length > 0) {
          const constituentMaxes: number[] = [];
          for (const c of info.constituents) {
            if (c.canFulfill === false) {
              constituentMaxes.push(0);
              continue;
            }
            const cInfo = stockAvailability[c.productId];
            if (cInfo && (!cInfo.isAvailable || (cInfo.maxQty !== undefined && cInfo.maxQty <= 0))) {
              constituentMaxes.push(0);
              continue;
            }
            const baseRegion = (c.region || 'MY').toUpperCase();
            const regions = cInfo
              ? regionsToTry(cInfo.defaultRegion, playerRegion, cInfo)
              : [baseRegion];

            let totalConstituentMax = 0;
            let foundChargedRegion = false;

            for (const tryRegion of regions) {
              const shellsPerUnit = cInfo
                ? shellCostForRegion(cInfo, tryRegion)
                : c.shellsPerUnit;

              const shellsPerBundleUnit = shellsPerUnit * c.qty;
              if (shellsPerBundleUnit <= 0) continue;
              foundChargedRegion = true;

              const pool = regionPools[tryRegion];
              if (!pool || pool.accounts === 0) continue;

              const sumAccountShells = pool.accountShells.reduce(
                (a, b) => a + b,
                0,
              );
              const aggregateAvailable = Math.max(
                0,
                sumAccountShells - (pool.publicReserved || 0),
              );

              const thisBundleCartQty = cart[prodId]?.qty || 0;
              const thisBundleRegionShells =
                (bundleRegionShells[prodId]?.[tryRegion] || 0) * thisBundleCartQty;
              const totalRegionCartShells = cartShellsByRegion[tryRegion] || 0;
              const otherItemsShells =
                totalRegionCartShells - thisBundleRegionShells;

              const remainingForThis = Math.max(
                0,
                aggregateAvailable - otherItemsShells,
              );
              totalConstituentMax += Math.floor(
                remainingForThis / shellsPerBundleUnit,
              );
            }

            constituentMaxes.push(
              foundChargedRegion ? totalConstituentMax : 99,
            );
          }

          if (constituentMaxes.length > 0) {
            apiMax = Math.min(apiMax, Math.min(...constituentMaxes));
          }
        }

        const currentQty = cart[prodId]?.qty || 0;
        const canAdd = currentQty < apiMax;

        let status: StockStatus;
        let reason = '';
        const remaining = apiMax - currentQty;

        if (apiMax <= 0) {
          status = 'out_of_stock';
          reason = 'Out of stock';
        } else if (remaining <= 0) {
          status = 'low_stock';
          reason = 'Max reached';
        } else if (remaining <= LOW_STOCK_THRESHOLD) {
          status = 'low_stock';
          reason = '';
        } else {
          status = 'available';
          reason = '';
        }

        feasibility[prodId] = {
          effectiveRegion: effRegion,
          maxQty: apiMax,
          canAdd,
          status,
          reason,
        };
        effectiveMaxQty[prodId] = apiMax;
        continue;
      }

      // ── Single product feasibility ─────────────────────────────────
      const shellsPerUnit = tentativeShells[prodId];
      const pool = regionPools[effRegion];
      const isFallback =
        effRegion !== (info.defaultRegion || 'MY').toUpperCase();

      // ── Free / zero-shell items ──────────────────────────────────
      if (shellsPerUnit <= 0) {
        const apiMax = info.maxQty;
        const currentQty = cart[prodId]?.qty || 0;
        const canAdd = currentQty < apiMax;
        feasibility[prodId] = {
          effectiveRegion: effRegion,
          maxQty: apiMax,
          canAdd,
          status: isFallback
            ? 'fallback_region'
            : canAdd
              ? 'available'
              : 'low_stock',
          reason: '',
        };
        effectiveMaxQty[prodId] = apiMax;
        continue;
      }

      // ── Aggregate available shells in this region ────────────────
      // aggregateAvailable = totalShells - reservedForPublicOrders
      // The pool.totalShells already reflects shellBalance - reservedShells
      // (the backend sends per-account (shellBalance - reservedShells) in accountShells).
      // Actually, pool.totalShells = sum of all account shellBalances (raw).
      // pool.accountShells[i] = shellBalance - reservedShells for account i.
      // So the true aggregate available = sum(accountShells) - publicReserved.
      const totalAvailableAccountsShells = pool
        ? pool.accountShells.reduce((a, b) => a + b, 0)
        : 0;
      const aggregateAvailable = Math.max(
        0,
        totalAvailableAccountsShells - (pool?.publicReserved || 0),
      );

      // Shells used by OTHER cart items in this region
      const thisProductCartQty = cart[prodId]?.qty || 0;
      const thisProductCartShells = shellsPerUnit * thisProductCartQty;
      const totalRegionCartShells = cartShellsByRegion[effRegion] || 0;
      const otherItemsShells = totalRegionCartShells - thisProductCartShells;

      const remainingForThisProduct = Math.max(
        0,
        aggregateAvailable - otherItemsShells,
      );
      const capacityMaxQty = Math.floor(
        remainingForThisProduct / shellsPerUnit,
      );

      // We bypass strict per-account matching here relying entirely on aggregate pool.
      // This allows the website to let users cart and order large-shell items,
      // which allows admins to intervene and handle manually if bot accounts lack individually
      // sufficient sizes. (The backend `apiMax` also bypasses this constraint now).
      const apiMax = info.maxQty;
      const maxQty = Math.min(apiMax, capacityMaxQty);
      const canAdd = thisProductCartQty < maxQty;

      // Determine status
      let status: StockStatus;
      let reason = '';
      const remaining = maxQty - thisProductCartQty;

      if (maxQty <= 0) {
        status = 'out_of_stock';
        reason = 'Out of stock';
      } else if (isFallback) {
        status = 'fallback_region';
        reason = '';
      } else if (remaining <= 0) {
        status = 'low_stock';
        reason = 'Max reached';
      } else if (remaining <= LOW_STOCK_THRESHOLD) {
        status = 'low_stock';
        reason = '';
      } else {
        status = 'available';
        reason = '';
      }

      feasibility[prodId] = {
        effectiveRegion: effRegion,
        maxQty,
        canAdd,
        status,
        reason,
      };
      effectiveMaxQty[prodId] = maxQty;
    }

    return { feasibility, effectiveMaxQty };
  }, [cart, stockAvailability, regionPools, playerRegion]);

  return result;
}
